import { createClient } from '@supabase/supabase-js'

// Use production Supabase (revert to original configuration)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key'
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY

// Check if we have real credentials
export const hasValidCredentials = !!(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY)

// Log environment status for debugging
if (typeof window !== 'undefined') {
  console.log('Environment status:', {
    hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
    hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
    hasServiceKey: !!supabaseServiceKey,
    hasValidCredentials
  })
}

// Regular client for normal operations
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Admin client that bypasses RLS for admin operations
export const supabaseAdmin = supabaseServiceKey 
  ? createClient(supabaseUrl, supabaseServiceKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        storageKey: 'sb-admin-auth-token'
      }
    })
  : null

// Database types
export interface Database {
  public: {
    Tables: {
      celebrities: {
        Row: {
          id: string
          name: string
          category: string
          days: string[]
          known_for: string
          agent: string
          agent_contact: string
          pr_available: string
          story_details?: string
          notes?: string
          source: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          category: string
          days: string[]
          known_for: string
          agent: string
          agent_contact: string
          pr_available: string
          story_details?: string
          notes?: string
          source?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          category?: string
          days?: string[]
          known_for?: string
          agent?: string
          agent_contact?: string
          pr_available?: string
          story_details?: string
          notes?: string
          source?: string
          created_at?: string
          updated_at?: string
        }
      }
      agents: {
        Row: {
          id: string
          name: string
          agency: string
          email: string
          phone: string
          guests: string[]
          total_requests: number
          source: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          agency: string
          email: string
          phone: string
          guests?: string[]
          total_requests?: number
          source?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          agency?: string
          email?: string
          phone?: string
          guests?: string[]
          total_requests?: number
          source?: string
          created_at?: string
          updated_at?: string
        }
      }
      journalists: {
        Row: {
          id: string
          name: string
          outlet: string
          email: string
          phone?: string
          secondary_outlets?: string
          specialty?: string
          bio?: string
          twitter?: string
          website?: string
          verified: boolean
          credentials_picked_up: boolean
          notes?: string
          source: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          name: string
          outlet: string
          email: string
          phone?: string
          secondary_outlets?: string
          specialty?: string
          bio?: string
          twitter?: string
          website?: string
          verified?: boolean
          credentials_picked_up?: boolean
          notes?: string
          source?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          name?: string
          outlet?: string
          email?: string
          phone?: string
          secondary_outlets?: string
          specialty?: string
          bio?: string
          twitter?: string
          website?: string
          verified?: boolean
          credentials_picked_up?: boolean
          notes?: string
          source?: string
          created_at?: string
          updated_at?: string
        }
      }
      interview_requests: {
        Row: {
          id: string
          journalist_id?: string
          journalist_name: string
          outlet: string
          celebrities: string[]
          status: string
          priority: any
          completed: any
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          journalist_id?: string
          journalist_name: string
          outlet: string
          celebrities: string[]
          status?: string
          priority?: any
          completed?: any
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          journalist_id?: string
          journalist_name?: string
          outlet?: string
          celebrities?: string[]
          status?: string
          priority?: any
          completed?: any
          created_at?: string
          updated_at?: string
        }
      }
      users: {
        Row: {
          id: string
          email: string
          name: string
          role: string
          outlet?: string
          department?: string
          permissions: string[]
          password_hash?: string
          temp_password?: string
          invitation_token?: string
          invitation_sent_at?: string
          invitation_status: string
          must_change_password: boolean
          invited_by?: string
          last_active: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          email: string
          name: string
          role: string
          outlet?: string
          department?: string
          permissions?: string[]
          password_hash?: string
          temp_password?: string
          invitation_token?: string
          invitation_sent_at?: string
          invitation_status?: string
          must_change_password?: boolean
          invited_by?: string
          last_active?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          email?: string
          name?: string
          role?: string
          outlet?: string
          department?: string
          permissions?: string[]
          password_hash?: string
          temp_password?: string
          invitation_token?: string
          invitation_sent_at?: string
          invitation_status?: string
          must_change_password?: boolean
          invited_by?: string
          last_active?: string
          created_at?: string
          updated_at?: string
        }
      }
      message_threads: {
        Row: {
          id: string
          type: 'press_gaggle' | 'group_thread'
          title: string
          description?: string
          created_by?: string
          created_at: string
          updated_at: string
          is_active: boolean
        }
        Insert: {
          id?: string
          type: 'press_gaggle' | 'group_thread'
          title: string
          description?: string
          created_by?: string
          created_at?: string
          updated_at?: string
          is_active?: boolean
        }
        Update: {
          id?: string
          type?: 'press_gaggle' | 'group_thread'
          title?: string
          description?: string
          created_by?: string
          created_at?: string
          updated_at?: string
          is_active?: boolean
        }
      }
      messages: {
        Row: {
          id: string
          thread_id: string
          sender_id: string
          content: string
          is_urgent: boolean
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          thread_id: string
          sender_id: string
          content: string
          is_urgent?: boolean
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          thread_id?: string
          sender_id?: string
          content?: string
          is_urgent?: boolean
          created_at?: string
          updated_at?: string
        }
      }
      message_participants: {
        Row: {
          id: string
          thread_id: string
          user_id: string
          joined_at: string
          last_read_at: string
          is_active: boolean
        }
        Insert: {
          id?: string
          thread_id: string
          user_id: string
          joined_at?: string
          last_read_at?: string
          is_active?: boolean
        }
        Update: {
          id?: string
          thread_id?: string
          user_id?: string
          joined_at?: string
          last_read_at?: string
          is_active?: boolean
        }
      }
    }
  }
}